/* Smart Living – premium layer. Vanilla JS, no dependencies. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.body.classList.contains('sl-no-motion');

  /* ---------- Scroll reveal ---------- */
  const revealAll = () => document.querySelectorAll('.sl-reveal').forEach((el) => el.classList.add('is-in'));
  const initReveal = () => {
    if (reduce || !('IntersectionObserver' in window)) return revealAll();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    document.querySelectorAll('.sl-reveal:not(.is-in)').forEach((el) => io.observe(el));
  };

  /* ---------- Hero slideshow (crossfade) ---------- */
  const initHero = (hero) => {
    if (hero.dataset.slReady) return;
    hero.dataset.slReady = '1';
    const slides = [...hero.querySelectorAll('.sl-hero__slide')];
    if (slides.length < 2) return;
    const dots = [...hero.querySelectorAll('[data-sl-dot]')];
    const pauseBtn = hero.querySelector('[data-sl-pause]');
    const delay = (parseInt(hero.dataset.speed, 10) || 6) * 1000;
    const auto = hero.dataset.autoplay === 'true' && !reduce;
    let index = 0, timer = null, hovering = false, userPaused = !auto;

    const show = (n) => {
      const prev = slides[index];
      index = (n + slides.length) % slides.length;
      const next = slides[index];
      if (prev === next) return;
      next.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      prev.inert = true;
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      next.inert = false;
      dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      stop();
      if (auto && !userPaused && !hovering && !document.hidden) timer = setInterval(() => show(index + 1), delay);
    };

    hero.querySelector('[data-sl-prev]')?.addEventListener('click', () => { show(index - 1); start(); });
    hero.querySelector('[data-sl-next]')?.addEventListener('click', () => { show(index + 1); start(); });
    dots.forEach((d) => d.addEventListener('click', () => { show(parseInt(d.dataset.slDot, 10)); start(); }));
    pauseBtn?.addEventListener('click', () => {
      userPaused = !userPaused;
      pauseBtn.setAttribute('aria-pressed', String(userPaused));
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      start();
    });
    hero.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hovering = true; stop(); } });
    hero.addEventListener('pointerleave', () => { hovering = false; start(); });
    hero.addEventListener('focusin', () => { hovering = true; stop(); });
    hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) { hovering = false; start(); } });
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { show(index - 1); }
      if (e.key === 'ArrowRight') { show(index + 1); }
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    let x0 = null, y0 = null;
    hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { show(index + (dx < 0 ? 1 : -1)); start(); }
      x0 = null;
    }, { passive: true });

    /* Theme editor: jump to the selected slide */
    document.addEventListener('shopify:block:select', (e) => {
      const i = slides.findIndex((s) => s.dataset.blockId === e.detail.blockId);
      if (i > -1) { userPaused = true; stop(); show(i); }
    });

    /* Warm the next image once the page is idle, so the first fade never shows a blank frame */
    const warm = () => slides[1]?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3500 }); else setTimeout(warm, 2500);

    start();
  };

  /* ---------- Cart free-delivery bar ---------- */
  const money = (cents, format) => {
    const v = cents / 100;
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) =>
      key.indexOf('no_decimals') > -1
        ? Math.round(v).toLocaleString('en-US')
        : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };
  const updateFreeDelivery = () => {
    const cfg = window.slFreeDelivery;
    document.querySelectorAll('[data-sl-fd]').forEach((el) => {
      if (!cfg || !cfg.threshold) { el.hidden = true; return; }
      const total = parseInt(el.dataset.total, 10) || 0;
      const remaining = cfg.threshold - total;
      const pct = Math.min(100, Math.round((total * 100) / cfg.threshold));
      const html = remaining > 0
        ? `You're <strong>${money(remaining, cfg.format)}</strong> away from free delivery`
        : `${cfg.celebrate ? '🎉 ' : ''}You've unlocked <strong>free delivery</strong>!`;
      const text = el.querySelector('.sl-fd__text');
      if (text && text.innerHTML !== html) text.innerHTML = html;
      const fill = el.querySelector('.sl-fd__fill');
      if (fill) fill.style.width = `${pct}%`;
      el.classList.toggle('sl-fd--done', remaining <= 0);
      el.hidden = false;
    });
  };
  const initFreeDelivery = () => {
    updateFreeDelivery();
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || drawer.dataset.slFd) return;
    drawer.dataset.slFd = '1';
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; updateFreeDelivery(); });
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-total'] });
  };

  const init = () => {
    document.querySelectorAll('[data-sl-hero]').forEach(initHero);
    initReveal();
    initFreeDelivery();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
